import type {
    KpiAxisConfiguration,
    KpiDataRange,
} from 'components/molecules/PackArchitecture/kpiAxisConfiguration.utils';
import { DEFAULT_KPI_DATA_RANGE, DEFAULT_UNIFORM_BIN_COUNT } from 'constants/ppa';

import type { PpaChartKpi, PpaChartResponse, PpaChartResponseBody } from './pricePackArchitecture.types';

const X_AXIS_MIN_KPI_IDS = ['x_axis_min'];
const X_AXIS_MAX_KPI_IDS = ['x_axis_max'];

const asResponseBody = (
    response: PpaChartResponse | null | undefined,
): PpaChartResponseBody | null => {
    if (!response || Array.isArray(response)) return null;
    return response;
};

const findKpiById = (kpis: PpaChartKpi[] | undefined, kpiIds: string[]): PpaChartKpi | undefined =>
    kpis?.find((entry) => kpiIds.includes(entry.kpi_id ?? ''));

const getKpiRawValue = (kpis: PpaChartKpi[] | undefined, kpiIds: string[]): number => {
    const kpi = findKpiById(kpis, kpiIds);
    if (!kpi) return 0;

    if (typeof kpi.raw_value === 'number' && Number.isFinite(kpi.raw_value)) {
        return kpi.raw_value;
    }

    const parsed = Number(kpi.value);
    return Number.isFinite(parsed) ? parsed : 0;
};

export const toKpiDataRange = (rawMin: number, rawMax: number): KpiDataRange => {
    const defaultMin = Math.floor(rawMin);
    const defaultMax = Math.ceil(rawMax);

    if (defaultMin >= defaultMax) {
        return { defaultMin, defaultMax: defaultMin + 1 };
    }

    return { defaultMin, defaultMax };
};

/** Reads KPI axis slider bounds from API `kpis` using the given min/max kpi ids. */
export const extractKpiAxisBoundsFromKpis = (
    kpis: PpaChartKpi[] | undefined,
    minKpiIds: string[],
    maxKpiIds: string[],
): KpiDataRange | undefined => {
    if (!kpis?.length) return undefined;

    const minKpi = findKpiById(kpis, minKpiIds);
    const maxKpi = findKpiById(kpis, maxKpiIds);
    if (!minKpi || !maxKpi) return undefined;

    const rawMin = getKpiRawValue(kpis, minKpiIds);
    const rawMax = getKpiRawValue(kpis, maxKpiIds);
    if (!Number.isFinite(rawMin) || !Number.isFinite(rawMax)) return undefined;

    return toKpiDataRange(rawMin, rawMax);
};

/** Reads PA-07 KPI axis slider bounds from chart API `kpis` (`x_axis_min` / `x_axis_max`). */
export const extractKpiAxisBoundsFromChartData = (
    response: PpaChartResponse | null | undefined,
    _fallback: KpiDataRange = DEFAULT_KPI_DATA_RANGE,
): KpiDataRange | undefined => {
    const body = asResponseBody(response);
    return extractKpiAxisBoundsFromKpis(body?.kpis, X_AXIS_MIN_KPI_IDS, X_AXIS_MAX_KPI_IDS);
};

/** Maps chart API `bin_config` to the KPI axis configuration modal state. */
export const extractKpiAxisConfigFromChartData = (
    response: PpaChartResponse | null | undefined,
    dataRange: KpiDataRange = DEFAULT_KPI_DATA_RANGE,
): KpiAxisConfiguration | undefined => {
    const binConfig = asResponseBody(response)?.bin_config;
    if (!binConfig) return undefined;

    const min =
        typeof binConfig.min === 'number' && Number.isFinite(binConfig.min)
            ? binConfig.min
            : dataRange.defaultMin;
    const max =
        typeof binConfig.max === 'number' && Number.isFinite(binConfig.max)
            ? binConfig.max
            : dataRange.defaultMax;
    const binningMethod: KpiAxisConfiguration['binningMethod'] =
        binConfig.method === 'custom' ? 'custom' : 'uniform';
    const binCount =
        typeof binConfig.bin_count === 'number' && binConfig.bin_count > 0
            ? binConfig.bin_count
            : DEFAULT_UNIFORM_BIN_COUNT;
    const breakpoints = Array.isArray(binConfig.breakpoints)
        ? binConfig.breakpoints.filter((value): value is number => Number.isFinite(value))
        : [];

    return { min, max, binningMethod, binCount, breakpoints };
};
